/**
 * GLView — General Ledger. One collapsible section per category/account with
 * opening balance → every transaction row (date, description, debit, credit,
 * running balance) → closing balance strip. Grand-total summary at the top.
 *
 * Groups coloured by accounting group (income / cogs / expense / other).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, Search, Flag, ExternalLink } from "lucide-react";
import { ReportShell } from "./atoms";
import { fmtMoney, fmtDate } from "@/lib/format";

const GROUP_COLORS = {
  income:  { bg: "#DCFCE7", fg: "#166534", label: "Income"  },
  cogs:    { bg: "#FFEDD5", fg: "#9A3412", label: "COGS"    },
  expense: { bg: "#FEE2E2", fg: "#991B1B", label: "Expense" },
  other:   { bg: "#E0E7FF", fg: "#3730A3", label: "Other"   },
};

export default function GLView({ gl, onExport, onShare, selectedCategories = [], onSelectedCategoriesChange, compareMode, onCompareModeChange }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState({});   // { category: true/false }
  const [picker, setPicker] = useState(false);
  const cmp = gl.comparative;

  const drillTo = (txnId) => {
    navigate(`/app/transactions?focus=${encodeURIComponent(txnId)}`);
  };

  // All categories that have appeared this period — used to populate the picker
  const availableCats = useMemo(
    () => (gl.accounts || []).map((a) => a.category),
    [gl.accounts],
  );
  const unpickedCats = availableCats.filter((c) => !selectedCategories.includes(c));

  const addCategory = (cat) => {
    if (!onSelectedCategoriesChange) return;
    onSelectedCategoriesChange([...selectedCategories, cat]);
    setPicker(false);
  };
  const removeCategory = (cat) => {
    if (!onSelectedCategoriesChange) return;
    onSelectedCategoriesChange(selectedCategories.filter((c) => c !== cat));
  };
  const clearAll = () => onSelectedCategoriesChange?.([]);

  const filteredAccounts = useMemo(() => {
    if (!filter.trim()) return gl.accounts;
    const q = filter.toLowerCase();
    return gl.accounts
      .map((a) => ({
        ...a,
        transactions: a.transactions.filter(
          (t) =>
            (t.description || "").toLowerCase().includes(q) ||
            (t.merchant || "").toLowerCase().includes(q) ||
            (t.memo || "").toLowerCase().includes(q),
        ),
      }))
      .filter((a) => a.category.toLowerCase().includes(q) || a.transactions.length > 0);
  }, [gl.accounts, filter]);

  const toggle = (cat) => setExpanded((e) => ({ ...e, [cat]: !e[cat] }));
  const expandAll = () => {
    const all = {};
    filteredAccounts.forEach((a) => (all[a.category] = true));
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  return (
    <ReportShell title="General Ledger" kind="gl" onExport={onExport} onShare={onShare} testid="gl-view">
      {/* Compare toggle */}
      {onCompareModeChange && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="gl-compare-toolbar">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            Compare
          </span>
          {["off", "prior_period", "prior_year"].map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => onCompareModeChange(mode === "off" ? null : mode)}
              data-testid={`gl-compare-${mode}`}
              className={`text-xs px-3 py-1.5 border ${
                (compareMode || "off") === mode
                  ? "bg-[#0b5d9e] text-white border-[#0b5d9e]"
                  : "hover:bg-[var(--ll-mint)]"
              }`}
              style={(compareMode || "off") === mode ? {} : { borderColor: "var(--ll-border)" }}
            >
              {mode === "off" ? "Off" : mode === "prior_period" ? "Prior period" : "Same period last year"}
            </button>
          ))}
        </div>
      )}

      {/* Comparative view */}
      {compareMode && cmp ? (
        <ComparativeTable cmp={cmp} />
      ) : compareMode && !cmp ? (
        <div className="border-l-4 border-amber-500 bg-amber-50 text-amber-800 px-4 py-3 mb-4 text-sm" data-testid="gl-compare-hint">
          Set a <strong>Period start</strong> and <strong>Period end</strong> above to see the comparative view.
        </div>
      ) : null}
      {!compareMode && (
      <>
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="gl-summary">
        <SummaryStat label="Accounts touched" value={gl.account_count} />
        <SummaryStat label="Transactions" value={gl.transaction_count} />
        <SummaryStat label="Total debits" value={fmtMoney(gl.grand_total_debit)} tone="warm" />
        <SummaryStat label="Total credits" value={fmtMoney(gl.grand_total_credit)} tone="cool" />
      </div>

      {/* Account multi-select — "Pick accounts…" so the user can share a slice */}
      {onSelectedCategoriesChange && (
        <div
          className="border mb-3 p-2.5 flex flex-wrap items-center gap-1.5"
          style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-card)" }}
          data-testid="gl-category-picker"
        >
          <span className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mr-1">
            Pick accounts
          </span>
          {selectedCategories.map((cat) => (
            <span
              key={cat}
              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-[#0b5d9e] text-white"
              data-testid={`gl-picked-${cat}`}
            >
              {cat}
              <button
                type="button"
                onClick={() => removeCategory(cat)}
                data-testid={`gl-pick-remove-${cat}`}
                className="hover:opacity-70"
                aria-label={`Remove ${cat}`}
              >
                ×
              </button>
            </span>
          ))}
          <div className="relative">
            <button
              type="button"
              onClick={() => setPicker((p) => !p)}
              disabled={unpickedCats.length === 0}
              data-testid="gl-pick-toggle"
              className="px-2 py-0.5 border text-xs hover:bg-[var(--ll-mint)] disabled:opacity-40"
              style={{ borderColor: "var(--ll-border)" }}
            >
              + Add account
            </button>
            {picker && unpickedCats.length > 0 && (
              <div
                className="absolute z-30 top-full left-0 mt-1 bg-white border shadow-lg max-h-60 overflow-y-auto min-w-[220px]"
                style={{ borderColor: "var(--ll-ink)" }}
                data-testid="gl-pick-menu"
              >
                {unpickedCats.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => addCategory(cat)}
                    data-testid={`gl-pick-option-${cat}`}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--ll-mint)]"
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
          {selectedCategories.length > 0 && (
            <button
              type="button"
              onClick={clearAll}
              data-testid="gl-pick-clear"
              className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-soft)] hover:underline ml-1"
            >
              Clear
            </button>
          )}
          {selectedCategories.length > 0 && (
            <span className="text-[11px] text-[var(--ll-ink-soft)] ml-auto">
              Showing {selectedCategories.length} of {availableCats.length} accounts
            </span>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ll-ink-soft)]" />
          <input
            type="text"
            placeholder="Filter by account, description, merchant…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full border pl-7 pr-3 py-1.5 text-sm"
            style={{ borderColor: "var(--ll-border)" }}
            data-testid="gl-filter"
          />
        </div>
        <button
          type="button"
          onClick={expandAll}
          className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="gl-expand-all"
        >
          Expand all
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)]"
          style={{ borderColor: "var(--ll-border)" }}
          data-testid="gl-collapse-all"
        >
          Collapse all
        </button>
      </div>

      {/* Accounts */}
      {filteredAccounts.length === 0 ? (
        <div className="text-center py-10 text-[var(--ll-ink-soft)] text-sm" data-testid="gl-empty">
          No accounts match the filter in this period.
        </div>
      ) : (
        <div className="space-y-2" data-testid="gl-accounts">
          {filteredAccounts.map((acc) => (
            <AccountSection
              key={acc.category}
              acc={acc}
              expanded={!!expanded[acc.category]}
              onToggle={() => toggle(acc.category)}
              onDrill={drillTo}
            />
          ))}
        </div>
      )}
      </>
      )}
    </ReportShell>
  );
}


function SummaryStat({ label, value, tone }) {
  const bg = tone === "warm" ? "#FFF7ED" : tone === "cool" ? "#EFF6FF" : "#F9FAFB";
  const fg = tone === "warm" ? "#9A3412" : tone === "cool" ? "#1E40AF" : "#1F2937";
  return (
    <div className="border p-3" style={{ background: bg, borderColor: "var(--ll-border)" }}>
      <div className="text-[9px] uppercase tracking-widest" style={{ color: fg, opacity: 0.75 }}>{label}</div>
      <div className="font-mono-fin text-lg font-semibold" style={{ color: fg }}>{value}</div>
    </div>
  );
}


function AccountSection({ acc, expanded, onToggle, onDrill }) {
  const colors = GROUP_COLORS[acc.group] || GROUP_COLORS.other;
  return (
    <div className="border" style={{ borderColor: "var(--ll-border)" }} data-testid={`gl-account-${acc.category}`}>
      {/* Header row (click to toggle) */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2.5 flex items-center gap-2 sm:gap-3 hover:bg-[var(--ll-mint)]"
        data-testid={`gl-toggle-${acc.category}`}
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-display font-bold text-sm flex-1 text-left truncate">{acc.category}</span>
        <span
          className="text-[10px] uppercase tracking-widest px-2 py-0.5 shrink-0 hidden sm:inline"
          style={{ background: colors.bg, color: colors.fg }}
        >
          {colors.label}
        </span>
        <span className="text-xs text-[var(--ll-ink-soft)] shrink-0 w-14 sm:w-16 text-right">
          {acc.transaction_count} txn
        </span>
        <span className="text-xs text-[var(--ll-ink-soft)] shrink-0 w-24 text-right font-mono-fin hidden sm:inline">
          Open {fmtMoney(acc.opening_balance)}
        </span>
        <span className="font-mono-fin font-bold text-sm shrink-0 w-24 sm:w-28 text-right">
          {fmtMoney(acc.closing_balance)}
        </span>
      </button>

      {expanded && (
        <div className="border-t overflow-x-auto" style={{ borderColor: "var(--ll-border)" }}>
          <table className="w-full text-sm">
            <thead className="bg-[#0b5d9e] text-white">
              <tr className="text-[10px] uppercase tracking-widest">
                <th className="px-3 py-2 text-left w-24">Date</th>
                <th className="px-3 py-2 text-left">Description</th>
                <th className="px-3 py-2 text-right w-24">Debit</th>
                <th className="px-3 py-2 text-right w-24">Credit</th>
                <th className="px-3 py-2 text-right w-28">Balance</th>
              </tr>
            </thead>
            <tbody>
              {/* Opening balance row */}
              <tr className="bg-amber-50 border-t" style={{ borderColor: "var(--ll-border)" }}>
                <td className="px-3 py-1.5 text-[11px] italic text-[var(--ll-ink-soft)]"></td>
                <td className="px-3 py-1.5 text-[11px] italic text-[var(--ll-ink-soft)]" colSpan={3}>
                  Opening balance
                </td>
                <td className="px-3 py-1.5 text-[11px] text-right font-mono-fin font-semibold">
                  {fmtMoney(acc.opening_balance)}
                </td>
              </tr>
              {acc.transactions.length === 0 ? (
                <tr className="border-t" style={{ borderColor: "var(--ll-border)" }}>
                  <td colSpan={5} className="px-3 py-3 text-xs text-center text-[var(--ll-ink-soft)]">
                    No transactions in this period.
                  </td>
                </tr>
              ) : acc.transactions.map((t) => (
                <tr
                  key={t.id}
                  className="border-t hover:bg-[var(--ll-bg-card)] cursor-pointer group"
                  style={{ borderColor: "var(--ll-border)" }}
                  data-testid={`gl-txn-${t.id}`}
                  onClick={() => onDrill?.(t.id)}
                  title="Open in Transactions page"
                >
                  <td className="px-3 py-1.5 text-xs font-mono-fin">{fmtDate(t.date)}</td>
                  <td className="px-3 py-1.5 text-xs">
                    <div className="font-medium flex items-center gap-1.5">
                      <span>{t.description}</span>
                      {t.flagged && <Flag size={10} className="text-red-600" />}
                      <ExternalLink
                        size={10}
                        className="opacity-0 group-hover:opacity-60 text-[#0b5d9e] shrink-0"
                      />
                    </div>
                    {t.merchant && t.merchant !== t.description && (
                      <div className="text-[10px] text-[var(--ll-ink-soft)]">{t.merchant}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-green-700">
                    {t.debit > 0 ? fmtMoney(t.debit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-red-700">
                    {t.credit > 0 ? fmtMoney(t.credit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin font-semibold">
                    {fmtMoney(t.running_balance)}
                  </td>
                </tr>
              ))}
              {/* Closing/totals row */}
              <tr className="border-t-2 bg-[var(--ll-bg-card)]" style={{ borderColor: "#0b5d9e" }}>
                <td className="px-3 py-2 text-[11px] font-bold uppercase tracking-widest" colSpan={2}>
                  Closing balance
                </td>
                <td className="px-3 py-2 text-xs text-right font-mono-fin font-bold text-green-700">
                  {fmtMoney(acc.total_debit)}
                </td>
                <td className="px-3 py-2 text-xs text-right font-mono-fin font-bold text-red-700">
                  {fmtMoney(acc.total_credit)}
                </td>
                <td className="px-3 py-2 text-sm text-right font-mono-fin font-bold">
                  {fmtMoney(acc.closing_balance)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function ComparativeTable({ cmp }) {
  const curTotalNet = cmp.current_total_debit - cmp.current_total_credit;
  const priTotalNet = cmp.prior_total_debit - cmp.prior_total_credit;
  const totalDelta = curTotalNet - priTotalNet;
  return (
    <div data-testid="gl-comparative">
      <div className="mb-4 border-l-4 border-[#0b5d9e] bg-[#EFF6FF] px-4 py-3 text-sm">
        <strong>{cmp.label}</strong> — comparing to{" "}
        <span className="font-mono-fin">
          {cmp.prior_start} → {cmp.prior_end}
        </span>
      </div>
      <div className="border overflow-x-auto" style={{ borderColor: "var(--ll-border)" }}>
        <table className="w-full text-sm">
          <thead className="bg-[#0b5d9e] text-white">
            <tr className="text-[10px] uppercase tracking-widest">
              <th className="px-3 py-2 text-left">Account</th>
              <th className="px-3 py-2 text-right w-24">Curr Debit</th>
              <th className="px-3 py-2 text-right w-24">Curr Credit</th>
              <th className="px-3 py-2 text-right w-24">Prior Debit</th>
              <th className="px-3 py-2 text-right w-24">Prior Credit</th>
              <th className="px-3 py-2 text-right w-24">Δ Net</th>
              <th className="px-3 py-2 text-right w-20">Δ %</th>
            </tr>
          </thead>
          <tbody>
            {(cmp.rows || []).map((r) => {
              const up = r.delta_net > 0;
              const down = r.delta_net < 0;
              const tone = r.group === "income" ? (up ? "good" : "bad") : (down ? "good" : "bad");
              const color = up ? (tone === "good" ? "#166534" : "#991B1B") : down ? (tone === "good" ? "#166534" : "#991B1B") : "#1F2937";
              return (
                <tr key={r.category} className="border-t hover:bg-[var(--ll-bg-card)]" style={{ borderColor: "var(--ll-border)" }} data-testid={`gl-compare-row-${r.category}`}>
                  <td className="px-3 py-1.5 text-xs">
                    <div className="font-medium">{r.category}</div>
                    <div className="text-[10px] text-[var(--ll-ink-soft)] uppercase tracking-widest">{r.group}</div>
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-green-700">
                    {r.current_debit > 0 ? fmtMoney(r.current_debit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-red-700">
                    {r.current_credit > 0 ? fmtMoney(r.current_credit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-[var(--ll-ink-soft)]">
                    {r.prior_debit > 0 ? fmtMoney(r.prior_debit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin text-[var(--ll-ink-soft)]">
                    {r.prior_credit > 0 ? fmtMoney(r.prior_credit) : ""}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin font-semibold" style={{ color }}>
                    {r.delta_net > 0 ? "+" : ""}{fmtMoney(r.delta_net)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-right font-mono-fin" style={{ color }}>
                    {r.delta_net_pct === null ? "—" : `${r.delta_net_pct > 0 ? "+" : ""}${r.delta_net_pct}%`}
                  </td>
                </tr>
              );
            })}
            {/* Grand totals */}
            <tr className="border-t-2 bg-[#0b5d9e] text-white" style={{ borderColor: "#0b5d9e" }}>
              <td className="px-3 py-2 text-[10px] uppercase tracking-widest font-bold">Grand totals</td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold">{fmtMoney(cmp.current_total_debit)}</td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold">{fmtMoney(cmp.current_total_credit)}</td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold opacity-80">{fmtMoney(cmp.prior_total_debit)}</td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold opacity-80">{fmtMoney(cmp.prior_total_credit)}</td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold">
                {totalDelta > 0 ? "+" : ""}{fmtMoney(totalDelta)}
              </td>
              <td className="px-3 py-2 text-right font-mono-fin font-bold">
                {priTotalNet ? `${totalDelta > 0 ? "+" : ""}${((totalDelta / Math.abs(priTotalNet)) * 100).toFixed(1)}%` : "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
